import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Settings, Mail, User, Send, AlertCircle, X, Link } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Loader from '../../components/ui/Loader';
import { EmailEditor } from 'react-email-editor';
import { useDispatch, useSelector } from 'react-redux';
import {
  getEmailTemplateRequest,
  createEmailTemplateRequest,
  updateEmailTemplateRequest,
  clearEmailTemplateCreateSuccess,
  clearEmailTemplateUpdateSuccess,
  clearEmailTemplateError,
  selectSelectedEmailTemplate,
  selectEmailTemplates,
  selectEmailTemplatesLoading,
} from 'store/emailTemplates';
import {
  sendTestEmailRequest,
  selectSendTestEmailLoading,
  selectSendTestEmailSuccess,
  selectSendTestEmailError,
  clearSendTestEmailState,
  selectSendTestEmailMessage,
} from 'store/emailSentHistory';
import {
  selectEmailTemplatesCreateSuccess,
  selectEmailTemplatesUpdateSuccess,
  selectEmailTemplatesError,
} from 'store/emailTemplates';
import {
  getVerifiedSenderAccountsRequest,
  selectVerifiedSenderAccounts,
  selectSenderAccountLoading,
} from 'store/senderAccounts';
import {
  CreateEmailTemplateRequest,
  UpdateEmailTemplateRequest,
} from 'types/emailTemplate/emailTemplate.types';
import {
  getWorkflowByCampaignRequest,
  selectSelectedWorkflow,
  updateWorkflowRequest,
} from 'store/workflows';
import { useToast } from 'contexts/ToastContext';
import { selectAttributes } from 'store/attributes';
import { getAttributesRequest } from 'store/attributes';
import {
  getEventAttributesRequest,
  selectEventAttributes,
} from 'store/eventAttributes';
import { unlayerConfig } from 'pages/EmailBuilder/unlayerConfig';
import EmailTemplatePreviewModal from './EmailTemplatePreviewModal';
import SendTestEmailModal from './SendTestEmailModal';
import ReviewLinksModal from './ReviewLinksModal';
import TemplateSelectionGrid from './TemplateSelectionGrid';
import { extractLinksFromHtml } from './emailBuilderLinkUtils';
import { useUser } from '../../hooks/useUser';
import { API_CONFIG } from '../../constants';
import { getCampaignRequest, selectSelectedCampaign } from 'store/campaigns';

const EmailBuilder: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const emailEditorRef = useRef<any>(null);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [templateData, setTemplateData] = useState({
    name: 'Untitled',
    replyTo: '',
    from: '',
    subject: '',
    unlayerConfig: {},
  });
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    templateName: string;
    htmlContent: string;
  }>({
    isOpen: false,
    templateName: '',
    htmlContent: '',
  });
  const [pendingDesign, setPendingDesign] = useState<any>(null);
  const [isTestEmailModalOpen, setIsTestEmailModalOpen] = useState(false);
  const [testEmailHtmlContent, setTestEmailHtmlContent] = useState<string>('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [isReviewLinksModalOpen, setIsReviewLinksModalOpen] = useState(false);
  const [reviewLinks, setReviewLinks] = useState<{ href: string }[]>([]);
  const { showToast } = useToast();
  const { getUserEmail } = useUser();

  // Redux selectors
  const createSuccess = useSelector(selectEmailTemplatesCreateSuccess);
  const updateSuccess = useSelector(selectEmailTemplatesUpdateSuccess);
  const selectedEmailTemplate = useSelector(selectSelectedEmailTemplate);
  const error = useSelector(selectEmailTemplatesError);
  const emailTemplates = useSelector(selectEmailTemplates);
  const emailTemplatesLoading = useSelector(selectEmailTemplatesLoading);
  const workflow = useSelector(selectSelectedWorkflow);
  const attributes = useSelector(selectAttributes);
  const eventAttributes = useSelector(selectEventAttributes);
  const verifiedSenderAccounts = useSelector(selectVerifiedSenderAccounts);
  const senderAccountsLoading = useSelector(selectSenderAccountLoading);
  const sendTestEmailLoading = useSelector(selectSendTestEmailLoading);
  const sendTestEmailSuccess = useSelector(selectSendTestEmailSuccess);
  const sendTestEmailError = useSelector(selectSendTestEmailError);
  const sendTestEmailMessage = useSelector(selectSendTestEmailMessage);
  const campaign = useSelector(selectSelectedCampaign);

  const { nodeId, templateId, campaignId } = useParams();

  useEffect(() => {
    // Clear any previous errors when component mounts or templateId changes
    dispatch(clearEmailTemplateError());
    if (campaignId) {
      dispatch(getCampaignRequest(campaignId));
      dispatch(getWorkflowByCampaignRequest(campaignId));
    }
    if (templateId) {
      dispatch(getEmailTemplateRequest(templateId));
    }
    // When !templateId, TemplateSelectionGrid fetches templates (with workspaceId)
    dispatch(getAttributesRequest());
    dispatch(getVerifiedSenderAccountsRequest());
  }, [templateId, dispatch, campaignId]);

  // Derive trigger type and event name from workflow (trigger node) or campaign
  const triggerType = React.useMemo(() => {
    const nodes = workflow?.draftFlow?.nodes ?? [];
    const triggerNode = nodes.find((n: any) => n.data?.type === 'trigger');
    return (triggerNode?.data?.config as any)?.trigger ?? campaign?.triggerType;
  }, [workflow?.draftFlow?.nodes, campaign?.triggerType]);

  const eventName = React.useMemo(() => {
    const nodes = workflow?.draftFlow?.nodes ?? [];
    const triggerNode = nodes.find((n: any) => n.data?.type === 'trigger');
    return (triggerNode?.data?.config as any)?.event ?? campaign?.event ?? '';
  }, [workflow?.draftFlow?.nodes, campaign?.event]);

  // Fetch event attributes when trigger is event and we have an event name
  useEffect(() => {
    if (triggerType === 'event' && eventName) {
      dispatch(getEventAttributesRequest(eventName));
    }
  }, [triggerType, eventName, dispatch]);

  useEffect(() => {
    if (selectedEmailTemplate && templateId) {
      setTemplateData({
        name: selectedEmailTemplate.name,
        replyTo: selectedEmailTemplate.replyTo || '',
        from: selectedEmailTemplate.from || '',
        subject: selectedEmailTemplate.subject || '',
        unlayerConfig: selectedEmailTemplate.unlayerConfig || {},
      });

      // Set pending design to load once editor is ready
      if (templateId && selectedEmailTemplate.unlayerConfig) {
        const designData =
          selectedEmailTemplate.unlayerConfig.design ||
          selectedEmailTemplate.unlayerConfig;
        setPendingDesign(designData);
        setIsTemplateLoading(true);
      }
    }
  }, [selectedEmailTemplate, templateId]);

  // Load pending design when editor becomes ready
  useEffect(() => {
    if (isEditorReady && pendingDesign) {
      const editor = getEditorInstance();
      if (editor) {
        setTimeout(() => {
          try {
            if (typeof editor.loadDesign === 'function') {
              editor.loadDesign(pendingDesign);
            } else {
              console.error('No loadDesign or loadJSON method available');
            }
            // Clear pending design after loading
            setPendingDesign(null);
            setIsTemplateLoading(false);
          } catch (error) {
            console.error('Error loading pending design:', error);
            setPendingDesign(null);
            setIsTemplateLoading(false);
          }
        }, 500);
      }
    }
  }, [isEditorReady, pendingDesign]);

  const handleUpdateWorkflow = useCallback(() => {
    if (!templateId && nodeId && workflow && campaignId) {
      const updatedDraftFlow = {
        ...workflow.draftFlow,
        nodes: workflow.draftFlow.nodes.map((node: any) => {
          if (node.id === nodeId) {
            return {
              ...node,
              data: {
                ...node.data,
                config: {
                  ...node.data.config,
                  templateId: selectedEmailTemplate?._id,
                },
              },
            };
          }
          return node;
        }),
      };

      dispatch(
        updateWorkflowRequest({
          campaignId: campaignId,
          draftFlow: updatedDraftFlow,
        })
      );
    }

    // Update URL to include templateId when creating a new template
    if (!templateId && selectedEmailTemplate?._id && campaignId && nodeId) {
      const newUrl = `/campaigns/${campaignId}/node/${nodeId}/email-builder/${selectedEmailTemplate._id}`;
      navigate(newUrl, { replace: true });
    }

    showToast({
      type: 'success',
      title: 'Success',
      message: 'Email template created successfully!',
    });
    setHasUnsavedChanges(false);
    setIsSaving(false);
    dispatch(clearEmailTemplateCreateSuccess());
  }, [
    templateId,
    nodeId,
    workflow,
    campaignId,
    selectedEmailTemplate,
    dispatch,
    showToast,
    navigate,
  ]);

  useEffect(() => {
    if (createSuccess) {
      handleUpdateWorkflow();
    }
  }, [createSuccess, handleUpdateWorkflow]);

  useEffect(() => {
    if (updateSuccess) {
      showToast({
        type: 'success',
        title: 'Success',
        message: 'Email template updated successfully!',
      });
      setHasUnsavedChanges(false);
      setIsSaving(false);
      dispatch(clearEmailTemplateUpdateSuccess());
    }
  }, [updateSuccess, dispatch, showToast]);

  useEffect(() => {
    if (error && isSaving) {
      showToast({
        type: 'error',
        title: 'Error',
        message: `Error ${templateId ? 'updating' : 'creating'} email template: ${error}`,
      });
      setIsSaving(false);
      dispatch(clearEmailTemplateError());
    }
  }, [error, isSaving, templateId, showToast, dispatch]);

  // Handle test email success
  useEffect(() => {
    if (sendTestEmailSuccess && sendTestEmailMessage) {
      showToast({
        type: 'success',
        title: 'Success',
        message: sendTestEmailMessage,
      });
      setIsTestEmailModalOpen(false);
      setTestEmailHtmlContent('');
      dispatch(clearSendTestEmailState());
    }
  }, [sendTestEmailSuccess, sendTestEmailMessage, showToast, dispatch]);

  // Handle test email error
  useEffect(() => {
    if (sendTestEmailError) {
      showToast({
        type: 'error',
        title: 'Error',
        message: sendTestEmailError,
      });
      dispatch(clearSendTestEmailState());
    }
  }, [sendTestEmailError, showToast, dispatch]);

  // Warn on tab close/refresh when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleBack = () => {
    const workflowPath = `/campaigns/builder/${campaignId}`;
    const leave = () =>
      navigate(workflowPath, { state: { openWorkflow: true } });

    if (hasUnsavedChanges) {
      if (
        window.confirm(
          'You have unsaved changes. Are you sure you want to leave?'
        )
      ) {
        leave();
      }
    } else {
      leave();
    }
  };

  const getEditorInstance = () => {
    return emailEditorRef.current?.editor;
  };

  const onLoad = () => {
    // Editor loaded
  };

  const onReady = () => {
    setTimeout(() => {
      setIsEditorReady(true);

      const editor = getEditorInstance();
      if (editor) {
        if (typeof editor.addEventListener === 'function') {
          editor.addEventListener('design:updated', () => {
            setHasUnsavedChanges(true);
            // Clear validation error when user makes changes
            setValidationError(null);
          });
        }
      }
      // If there's no pending design, template is already loaded
      if (!pendingDesign) {
        setIsTemplateLoading(false);
      }
    }, 1000);
  };

  const handleSaveAndContinue = () => {
    const editor = getEditorInstance();

    if (!templateData.name.trim() || templateData.name === 'Untitled') {
      showToast({
        type: 'error',
        title: 'Validation Error',
        message: 'Please change the template name to save the template.',
      });
      return;
    }

    if (!editor || !isEditorReady) {
      showToast({
        type: 'error',
        title: 'Editor Not Ready',
        message: 'Editor is not ready yet. Please wait a moment and try again.',
      });
      return;
    }

    if (isSaving) {
      return;
    }

    // Clear any previous errors before making the API call
    dispatch(clearEmailTemplateError());
    setIsSaving(true);

    editor.exportHtml((data: any) => {
      const { html } = data;

      if (!html) {
        showToast({
          type: 'error',
          title: 'Export Error',
          message: 'Unable to generate HTML content. Please try again.',
        });
        setIsSaving(false);
        return;
      }

      // Check if both required unsubscribe links exist in the HTML
      const hasUnsubscribeLink = html.includes('{{unsubscribe_link}}');

      // Build validation error message
      const missingLinks: string[] = [];
      if (!hasUnsubscribeLink) {
        missingLinks.push('{{unsubscribe_link}}');
      }

      if (missingLinks.length > 0) {
        const errorMessage = `Please add the following required link${missingLinks.length > 1 ? 's' : ''} to your email template: ${missingLinks.join(' and ')}`;
        setValidationError(errorMessage);
        setIsSaving(false);
        return;
      }

      // Clear validation error if both links are present
      setValidationError(null);

      const trackingPixel = `<img src="${API_CONFIG.SERVER_URL}track-events/email-opened?id={{sentId}}" alt="" title="" style="" width="1"/>`;

      const modifiedHtml = html + trackingPixel;

      const modifiedData = {
        ...data,
        html: modifiedHtml,
      };

      if (templateId) {
        // Update existing template
        const updateRequest: UpdateEmailTemplateRequest = {
          name: templateData.name,
          replyTo: templateData.replyTo || '',
          from: templateData.from || '',
          subject: templateData.subject || '',
          unlayerConfig: modifiedData,
        };
        dispatch(updateEmailTemplateRequest(templateId, updateRequest));
      } else {
        // Create new template
        const templateRequest: CreateEmailTemplateRequest = {
          name: templateData.name,
          replyTo: templateData.replyTo || '',
          from: templateData.from || '',
          subject: templateData.subject || '',
          campaignId: campaignId || '',
          unlayerConfig: modifiedData,
        };
        dispatch(createEmailTemplateRequest(templateRequest));
      }
    });
  };

  const isEditorView =
    templateId || Object.keys(templateData.unlayerConfig).length > 0;
  const pageTitle = templateId
    ? 'Edit Email Template'
    : 'Email Template Library';
  const pageSubtitle = templateId
    ? 'Update design and settings for this email'
    : 'Choose a template or start from scratch';

  return (
    <div className="bg-background flex flex-col min-h-[calc(100vh-4rem)]">
      {/* Page header */}
      <div className="bg-card border-b border-border px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-semibold text-foreground truncate">
                  {pageTitle}
                </h1>
                {hasUnsavedChanges && isEditorView && (
                  <span className="text-xs text-warning-foreground bg-warning-background px-2 py-0.5 rounded-md font-medium">
                    Unsaved changes
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">
                {pageSubtitle}
              </p>
            </div>
          </div>
          {isEditorView && (
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="small"
                onClick={handleBack}
                disabled={!isEditorReady}
                className="hidden sm:inline-flex"
              >
                Back to Workflow
              </Button>
              <Button
                variant="primary"
                size="small"
                disabled={!isEditorReady || isSaving}
                onClick={handleSaveAndContinue}
              >
                {isSaving ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-primary-foreground border-t-transparent" />
                    Saving...
                  </span>
                ) : (
                  'Save'
                )}
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        {!templateId && Object.keys(templateData.unlayerConfig).length === 0 ? (
          /* Email Template Library */
          <div className="flex-1 p-6 overflow-auto">
            <div className="max-w-6xl mx-auto">
              <TemplateSelectionGrid
                blankUnlayerConfig={unlayerConfig}
                emailTemplates={emailTemplates}
                emailTemplatesLoading={emailTemplatesLoading}
                onSelectBlank={(config) => {
                  const designData = config.design || config;
                  setPendingDesign(designData);
                  setIsTemplateLoading(true);
                  setTemplateData((prev) => ({
                    ...prev,
                    unlayerConfig: config,
                  }));
                }}
                onSelectTemplate={(template) => {
                  const designData =
                    template.unlayerConfig?.design || template.unlayerConfig;
                  setPendingDesign(designData);
                  setIsTemplateLoading(true);
                  setTemplateData((prev) => ({
                    ...prev,
                    unlayerConfig: template.unlayerConfig ?? {},
                  }));
                }}
                onPreviewTemplate={(templateName, htmlContent) => {
                  setPreviewModal({
                    isOpen: true,
                    templateName,
                    htmlContent,
                  });
                }}
              />
            </div>
          </div>
        ) : (
          // Email Editor (existing code)
          <>
            {/* Validation Error Banner */}
            {validationError && (
              <div className="bg-destructive-background border-b border-destructive/30 px-6 py-3">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-foreground mb-0.5">
                      Missing required links
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {validationError}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setValidationError(null)}
                    className="text-muted-foreground hover:text-foreground flex-shrink-0 rounded p-1 transition-colors"
                    aria-label="Dismiss error"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
            {/* Template Settings */}
            <div className="bg-card border-b border-border shadow-sm">
              <div className="px-6 py-4">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-primary/10 rounded-lg">
                      <Settings className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">
                        Template settings
                      </h3>
                    </div>
                    {selectedEmailTemplate?.status && (
                      <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded capitalize">
                        {selectedEmailTemplate.status}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => {
                        const editor = getEditorInstance();
                        if (editor && isEditorReady) {
                          editor.exportHtml((data: any) => {
                            const html = data?.html ?? '';
                            setReviewLinks(extractLinksFromHtml(html));
                            setIsReviewLinksModalOpen(true);
                          });
                        } else {
                          const savedHtml =
                            selectedEmailTemplate?.unlayerConfig?.html;
                          if (savedHtml) {
                            setReviewLinks(extractLinksFromHtml(savedHtml));
                            setIsReviewLinksModalOpen(true);
                          } else {
                            showToast({
                              type: 'error',
                              title: 'Cannot review links',
                              message:
                                'Editor is not ready or template has no content. Try again in a moment or save the template first.',
                            });
                          }
                        }
                      }}
                      disabled={
                        !isEditorReady &&
                        !selectedEmailTemplate?.unlayerConfig?.html
                      }
                      className="shrink-0 gap-2"
                    >
                      <Link className="w-4 h-4" />
                      Review Links
                    </Button>
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => {
                        const savedHtml =
                          selectedEmailTemplate?.unlayerConfig?.html;
                        if (savedHtml) {
                          setTestEmailHtmlContent(savedHtml);
                          setIsTestEmailModalOpen(true);
                        } else {
                          showToast({
                            type: 'error',
                            title: 'No saved content',
                            message:
                              'Save the email template first before sending a test email.',
                          });
                        }
                      }}
                      className="shrink-0 gap-2"
                    >
                      <Send className="w-4 h-4" />
                      Send test email
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Input
                    label="Template name"
                    required
                    size="sm"
                    value={templateData.name}
                    onChange={(e) =>
                      setTemplateData((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    className="px-2 py-1.5 bg-background"
                    placeholder="Enter template name"
                    error={
                      !templateId && !templateData.name.trim()
                        ? 'Required to save'
                        : undefined
                    }
                  />
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-foreground">
                      Subject line
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                      <input
                        type="text"
                        value={templateData.subject}
                        onChange={(e) =>
                          setTemplateData((prev) => ({
                            ...prev,
                            subject: e.target.value,
                          }))
                        }
                        className="w-full pl-8 pr-2.5 py-1.5 text-xs border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors hover:border-input bg-background"
                        placeholder="Enter email subject"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-foreground">
                      From account
                    </label>
                    <div className="relative">
                      <User className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                      <select
                        value={templateData.from}
                        onChange={(e) =>
                          setTemplateData((prev) => ({
                            ...prev,
                            from: e.target.value,
                          }))
                        }
                        className="w-full pl-8 pr-2.5 py-1.5 text-xs border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors hover:border-input appearance-none bg-background disabled:bg-muted disabled:cursor-not-allowed"
                        disabled={senderAccountsLoading}
                      >
                        <option value="">Select sender account</option>
                        {verifiedSenderAccounts?.map((account) => (
                          <option key={account._id} value={account._id}>
                            {account.name} ({account.email}){/* –{' '} */}
                            {/* {account.providerType} */}
                          </option>
                        ))}
                      </select>
                    </div>
                    {senderAccountsLoading && (
                      <p className="text-muted-foreground text-xs mt-0.5">
                        Loading…
                      </p>
                    )}
                    {!senderAccountsLoading &&
                      verifiedSenderAccounts?.length === 0 && (
                        <p className="text-warning-foreground text-xs mt-0.5">
                          No verified accounts
                        </p>
                      )}
                  </div>
                  <Input
                    label="Reply-to email"
                    type="email"
                    size="sm"
                    value={templateData.replyTo}
                    onChange={(e) =>
                      setTemplateData((prev) => ({
                        ...prev,
                        replyTo: e.target.value,
                      }))
                    }
                    placeholder="reply@example.com"
                    className="px-2 py-1.5 bg-background"
                  />
                </div>
              </div>
            </div>
            <div className="relative">
              {/* Loader Overlay */}
              {(!isEditorReady || isTemplateLoading) && (
                <div className="absolute inset-0 bg-background/90 backdrop-blur-sm flex items-center justify-center z-50">
                  <Loader size="medium" showText={false} />
                </div>
              )}
              <EmailEditor
                ref={emailEditorRef}
                onLoad={onLoad}
                onReady={onReady}
                minHeight={'calc(80vh - 60px)'}
                options={{
                  editor: {
                    confirmOnDelete: true,
                  },
                  version: '1.7.0',
                  displayMode: 'email',
                  devices: ['desktop', 'mobile', 'tablet'],
                  appearance: {
                    actionBar: {
                      placement: 'top',
                    },
                    panels: {
                      tools: {
                        collapsible: true,
                      },
                    },
                  },
                  features: {
                    blocks: true,
                    ai: {
                      magicImage: false,
                      smartText: true,
                      smartButtons: true,
                      smartHeadings: true,
                    },
                    userUploads: {
                      enabled: true,
                      search: true,
                    },
                    stockImages: {
                      enabled: true,
                      safeSearch: true,
                      defaultSearchTerm: 'nature',
                    },
                    textEditor: {
                      tables: true,
                      cleanPaste: true,
                    },
                  },
                  tools: {
                    social: {
                      enabled: true,
                    },
                    menu: {
                      enabled: false,
                    },
                    form: {
                      enabled: true,
                    },
                    video: {
                      enabled: true,
                    },
                    timer: {
                      enabled: true,
                    },
                    socialGroup: {
                      enabled: true,
                    },
                    imageEditor: {
                      enabled: true,
                    },
                  },
                  mergeTags: {
                    // Common contact fields
                    Email: {
                      value: '{{contact.email}}',
                    },
                    'First Name': {
                      value: '{{contact.firstName}}',
                    },
                    'Last Name': {
                      value: '{{contact.lastName}}',
                    },
                    'Custom ID': {
                      value: '{{contact.custom_id}}',
                    },
                    'Created At': {
                      value: '{{contact.createdAt}}',
                    },
                    // Dynamic attributes from the system
                    ...(attributes?.attributes?.reduce(
                      (acc: any, attr: any) => {
                        acc[attr.name] = {
                          value: `{{contact.${attr.name}}}`,
                        };
                        return acc;
                      },
                      {} as Record<
                        string,
                        { name: string; value: string; sample: string }
                      >
                    ) || {}),
                    // Event attributes (when triggerType is event) – use event. prefix
                    ...(triggerType === 'event' &&
                    eventAttributes?.attributes?.length
                      ? eventAttributes.attributes
                          .filter((attr: any) => !attr.isDeleted)
                          .reduce(
                            (acc: any, attr: any) => {
                              acc[`Event: ${attr.name}`] = {
                                value: `{{event.${attr.name}}}`,
                              };
                              return acc;
                            },
                            {} as Record<
                              string,
                              { name: string; value: string; sample: string }
                            >
                          )
                      : {}),
                    'Unsubscribe Link': {
                      value: '{{unsubscribe_link}}',
                    },
                    'Unsubscribe Preference Link': {
                      value: '{{unsubscribe_preference_link}}',
                    },
                  },
                }}
                appearance={{
                  theme: 'modern_light',
                }}
              />
            </div>
          </>
        )}
      </div>

      {/* Email Template Preview Modal */}
      <EmailTemplatePreviewModal
        isOpen={previewModal.isOpen}
        onClose={() =>
          setPreviewModal({
            isOpen: false,
            templateName: '',
            htmlContent: '',
          })
        }
        templateName={previewModal.templateName}
        htmlContent={previewModal.htmlContent}
      />

      {/* Send Test Email Modal */}
      <SendTestEmailModal
        isOpen={isTestEmailModalOpen}
        onClose={() => {
          setIsTestEmailModalOpen(false);
          setTestEmailHtmlContent('');
        }}
        verifiedSenderAccounts={verifiedSenderAccounts}
        senderAccountsLoading={senderAccountsLoading}
        userEmail={getUserEmail()}
        initialFromAccountId={templateData.from}
        htmlContent={testEmailHtmlContent}
        sendingTestEmail={sendTestEmailLoading}
        onSend={(fromAccountId, contactId, emailAddress) => {
          if (!templateId) {
            showToast({
              type: 'error',
              title: 'Error',
              message:
                'Please save the email template first before sending a test email.',
            });
            return;
          }
          if (!fromAccountId || !contactId) {
            showToast({
              type: 'error',
              title: 'Error',
              message: 'Please select both sender account and contact.',
            });
            return;
          }
          dispatch(
            sendTestEmailRequest(
              fromAccountId,
              templateId,
              contactId,
              emailAddress
            )
          );
        }}
      />

      {/* Review Links Modal */}
      <ReviewLinksModal
        isOpen={isReviewLinksModalOpen}
        onClose={() => {
          setIsReviewLinksModalOpen(false);
          setReviewLinks([]);
        }}
        links={reviewLinks}
      />
    </div>
  );
};

export default EmailBuilder;
